/**
 * Pure, synchronous chrome link gate (consolidation plan §0.16 [A-13]).
 *
 * These helpers used to live in components/site-footer.tsx. They moved here —
 * a dependency-free module with NO "use client" directive — so BOTH trees can
 * call them: server components (components/seo/collection-llm-content.tsx),
 * client components (components/site-header.tsx, the footer link lists, the
 * collection-page related block), and tests. site-footer re-exports them for
 * backward compatibility.
 *
 * The async half of the gate — resolving WHICH slugs are indexed — lives in
 * lib/collection-links.ts (filterLinkableCollections / getIndexedChromeSlugs).
 */

/**
 * Extract the collection slug from an internal `/collections/<slug>` href, or
 * `null` for any other href. The tiered index `/collections/all` is treated as
 * a non-collection link (always linkable) — it is the keeper-link surface
 * itself, never a gated collection page.
 */
export function collectionSlugFromHref(href: string): string | null {
  const match = /^\/collections\/([^/?#]+)/.exec(href)
  if (!match) return null
  return match[1] === "all" ? null : match[1]
}

/**
 * The seo_disposition gate for chrome links (P04 [A-13]). Drops any
 * `/collections/<slug>` link whose collection is NOT in the confirmed-indexed
 * set — so no non-indexed collection is ever linked from the header/footer.
 * Non-collection links (hub pages, tools, `/collections/all`) always pass.
 *
 * `indexedCollectionSlugs` is the set of collection slugs a SERVER parent has
 * confirmed `status='active' ∧ seo_disposition='indexed'` via
 * `lib/collection-links.filterLinkableCollections` — site-wide it arrives from
 * the root layout through `getIndexedChromeSlugs()` +
 * `IndexedChromeSlugsProvider`. When it is null/undefined (no set resolved —
 * e.g. the DB check failed with no cached value) the links pass through
 * unchanged: the chrome collection lists are the curated indexed T1 set and
 * were verified all-indexed at wiring time. Fails closed once a set is present
 * — a slug absent from the set is dropped, never linked.
 */
export function keepIndexedCollectionLinks<T extends { href: string }>(
  items: T[],
  indexedCollectionSlugs: ReadonlySet<string> | null | undefined
): T[] {
  if (!indexedCollectionSlugs) return items
  return items.filter((item) => {
    const slug = collectionSlugFromHref(item.href)
    return slug === null || indexedCollectionSlugs.has(slug)
  })
}
